/**
 * Domain Rating Requirements to Rank Page 1 — /data/domain-rating-benchmarks-b2b
 * Median, P25, P75 DR of top-10 results across 11 B2B industries
 */
import React, { useState, useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ErrorBar,
} from "recharts";
import SiteLayout from "@/components/SiteLayout";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { datasetJsonLd } from "@/lib/datasetSchema";
import { useIsMobile } from "@/hooks/useIsMobile";
import { VIDEO_REVIEW_URL as STRATEGIST_URL } from "@/lib/cta";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";
const cyan = "#57d7e2";

interface DRRow {
  industry: string;
  medianDR: number;
  p25DR: number;
  p75DR: number;
  minDR: number;
  maxDR: number;
  difficultyRating: "Medium" | "Hard" | "Very Hard";
  sampleKeywords: string[];
}

const DATA: DRRow[] = [
  { industry: "SaaS / B2B Software", medianDR: 78, p25DR: 68, p75DR: 88, minDR: 52, maxDR: 95, difficultyRating: "Very Hard", sampleKeywords: ["crm software", "project management software", "marketing automation"] },
  { industry: "Fintech", medianDR: 82, p25DR: 72, p75DR: 90, minDR: 58, maxDR: 96, difficultyRating: "Very Hard", sampleKeywords: ["accounting software", "payment gateway", "banking software"] },
  { industry: "HR Tech", medianDR: 67, p25DR: 55, p75DR: 79, minDR: 38, maxDR: 92, difficultyRating: "Hard", sampleKeywords: ["hr software", "payroll software", "ats software"] },
  { industry: "Cybersecurity", medianDR: 71, p25DR: 60, p75DR: 82, minDR: 41, maxDR: 93, difficultyRating: "Hard", sampleKeywords: ["endpoint security software", "siem tools", "identity management"] },
  { industry: "E-commerce", medianDR: 58, p25DR: 45, p75DR: 73, minDR: 30, maxDR: 90, difficultyRating: "Hard", sampleKeywords: ["ecommerce platform", "ecommerce software", "shopping cart software"] },
  { industry: "Legal Tech", medianDR: 61, p25DR: 50, p75DR: 74, minDR: 34, maxDR: 88, difficultyRating: "Hard", sampleKeywords: ["contract management software", "legal software", "clm tools"] },
  { industry: "Health Tech", medianDR: 74, p25DR: 63, p75DR: 84, minDR: 44, maxDR: 94, difficultyRating: "Very Hard", sampleKeywords: ["ehr software", "telemedicine software", "healthcare crm"] },
  { industry: "EdTech", medianDR: 63, p25DR: 50, p75DR: 76, minDR: 35, maxDR: 90, difficultyRating: "Hard", sampleKeywords: ["learning management system", "elearning software", "lms comparison"] },
  { industry: "Logistics & Supply Chain", medianDR: 56, p25DR: 43, p75DR: 70, minDR: 28, maxDR: 87, difficultyRating: "Hard", sampleKeywords: ["warehouse management system", "logistics software", "tms software"] },
  { industry: "Real Estate / PropTech", medianDR: 65, p25DR: 53, p75DR: 77, minDR: 36, maxDR: 91, difficultyRating: "Hard", sampleKeywords: ["property management software", "real estate crm", "tenant software"] },
  { industry: "Data & Analytics", medianDR: 76, p25DR: 65, p75DR: 87, minDR: 48, maxDR: 95, difficultyRating: "Very Hard", sampleKeywords: ["business intelligence software", "data analytics platform", "etl tools"] },
];

type SortCol = "industry" | "medianDR" | "p25DR" | "p75DR" | "minDR" | "maxDR";

function diffColors(r: DRRow["difficultyRating"]): { bg: string; color: string; border: string } {
  switch (r) {
    case "Medium": return { bg: "rgba(245,158,11,0.1)", color: "#f59e0b", border: "rgba(245,158,11,0.25)" };
    case "Hard": return { bg: "rgba(239,68,68,0.1)", color: "#ef4444", border: "rgba(239,68,68,0.25)" };
    case "Very Hard": return { bg: "rgba(153,27,27,0.15)", color: "#fca5a5", border: "rgba(153,27,27,0.3)" };
  }
}

function drColor(dr: number): string {
  if (dr >= 75) return "#ef4444";
  if (dr >= 60) return "#f59e0b";
  return "#22c55e";
}

function SortArrow({ active, dir }: { active: boolean; dir: "asc" | "desc" }) {
  if (!active) return <span style={{ color: "rgba(255,255,255,0.2)", fontSize: "10px", marginLeft: "4px" }}>⇅</span>;
  return <span style={{ color: cyan, fontSize: "10px", marginLeft: "4px" }}>{dir === "asc" ? "↑" : "↓"}</span>;
}

const CHART_DATA = DATA.map(d => ({
  name: d.industry.length > 14 ? d.industry.slice(0, 14) + "…" : d.industry,
  fullName: d.industry,
  medianDR: d.medianDR,
  range: [d.p25DR, d.p75DR],
}));

const overallMedian = Math.round(DATA.reduce((s, d) => s + d.medianDR, 0) / DATA.length);

export default function DomainRatingBenchmarks() {
  const isMobile = useIsMobile();
  const [search, setSearch] = useState("");
  const [diffFilter, setDiffFilter] = useState("All");
  const [sortCol, setSortCol] = useState<SortCol>("medianDR");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  useSeoMeta({
    title: "Domain Rating Requirements to Rank Page 1 by B2B Category 2026 — Indexed Data",
    description: "What DR does your site need to rank in the top 10? Median, P25, and P75 domain ratings across 11 B2B industries. Ahrefs data, March 2026.",
    canonical: "/data/domain-rating-benchmarks-b2b",
    jsonLd: datasetJsonLd({ title: "Domain Rating Requirements to Rank Page 1 by B2B Category 2026 — Indexed Data", description: "What DR does your site need to rank in the top 10? Median, P25, and P75 domain ratings across 11 B2B industries. Ahrefs data, March 2026.", path: "/data/domain-rating-benchmarks-b2b" }),
    ogType: "article",
  });

  function handleSort(col: SortCol) {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  }

  const filtered = useMemo(() => {
    let d = [...DATA];
    if (search.trim()) { const q = search.toLowerCase(); d = d.filter(r => r.industry.toLowerCase().includes(q)); }
    if (diffFilter !== "All") d = d.filter(r => r.difficultyRating === diffFilter);
    d.sort((a, b) => {
      const m = sortDir === "asc" ? 1 : -1;
      if (sortCol === "industry") return m * a.industry.localeCompare(b.industry);
      return m * (a[sortCol] - b[sortCol]);
    });
    return d;
  }, [search, diffFilter, sortCol, sortDir]);

  const selectStyle: React.CSSProperties = {
    background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "8px", color: "white", fontFamily: ff, fontSize: "14px",
    padding: "10px 32px 10px 14px", outline: "none", cursor: "pointer",
    appearance: "none", WebkitAppearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M2 4l4 4 4-4' stroke='rgba(255,255,255,0.4)' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center",
  };
  const thStyle: React.CSSProperties = {
    padding: "13px 16px", textAlign: "left" as const, fontSize: "11px", fontWeight: 700,
    fontFamily: ff, letterSpacing: "0.07em", textTransform: "uppercase" as const,
    color: "rgba(255,255,255,0.4)", borderBottom: "1px solid rgba(255,255,255,0.07)",
    whiteSpace: "nowrap" as const, cursor: "pointer", userSelect: "none" as const,
  };
  const tdStyle: React.CSSProperties = { padding: "16px", verticalAlign: "middle", borderBottom: "1px solid rgba(255,255,255,0.04)" };

  return (
    <SiteLayout>
      {/* Hero */}
      <section style={{ background: "#0a0a0a", padding: isMobile ? "140px 20px 56px" : "180px 72px 72px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "28px" }}>
            <a href="/data" style={{ color: "rgba(255,255,255,0.35)", fontSize: "12px", fontFamily: ff, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", textDecoration: "none" }} onMouseEnter={e => (e.currentTarget.style.color = cyan)} onMouseLeave={e => (e.currentTarget.style.color = "rgba(255,255,255,0.35)")}>Data</a>
            <span style={{ color: "rgba(255,255,255,0.2)", fontSize: "12px" }}>/</span>
            <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "12px", fontFamily: ff, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>Domain Rating Benchmarks</span>
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "4px 12px", borderRadius: "4px", background: "rgba(87,215,226,0.08)", border: "1px solid rgba(87,215,226,0.18)", color: cyan, fontSize: "11px", fontWeight: 700, fontFamily: ff, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "20px" }}>
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><circle cx="4" cy="4" r="3" fill={cyan} /></svg>
            Original Data
          </div>
          <h1 style={{ color: "white", fontSize: isMobile ? "28px" : "clamp(34px, 4vw, 50px)", fontWeight: 800, fontFamily: ff, lineHeight: 1.1, letterSpacing: "-0.02em", marginBottom: "16px", maxWidth: "780px" }}>
            Domain Rating Requirements to Rank Page 1 by B2B Category 2026
          </h1>
          <p style={{ color: "rgba(255,255,255,0.5)", fontSize: isMobile ? "16px" : "18px", fontFamily: ff, lineHeight: 1.65, maxWidth: "680px", marginBottom: "48px" }}>
            What DR does your site need before SEO investment pays off? Median and interquartile range of domain ratings across the top-10 results for 5 keywords per industry. 550 data points, March 2026.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, auto)", gap: isMobile ? "24px 32px" : "0 56px" }}>
            {[
              { value: `DR ${overallMedian}`, label: "median DR required across all B2B categories" },
              { value: `DR ${DATA.find(d => d.industry === "Fintech")?.medianDR}`, label: "median DR needed in Fintech — the hardest category" },
              { value: `DR ${DATA.find(d => d.industry === "Logistics & Supply Chain")?.medianDR}`, label: "median DR in Logistics — the most accessible" },
            ].map(s => (
              <div key={s.label}>
                <div style={{ color: cyan, fontSize: isMobile ? "28px" : "34px", fontWeight: 800, fontFamily: ff, lineHeight: 1, marginBottom: "4px" }}>{s.value}</div>
                <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "13px", fontFamily: ff, fontWeight: 500 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Chart */}
      <section style={{ background: "#0a0a0a", padding: isMobile ? "40px 20px" : "60px 72px" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <h2 style={{ color: "white", fontSize: isMobile ? "20px" : "26px", fontWeight: 700, fontFamily: ff, marginBottom: "8px" }}>Median Domain Rating to rank top 10 by industry</h2>
          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "14px", fontFamily: ff, marginBottom: "32px" }}>Error bars show P25–P75 range</p>
          <div style={{ height: "300px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={CHART_DATA} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="name" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10, fontFamily: ff }} />
                <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11, fontFamily: ff }} domain={[0, 100]} />
                <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", fontFamily: ff, fontSize: "13px" }} labelFormatter={(l) => CHART_DATA.find(d => d.name === l)?.fullName ?? l} labelStyle={{ color: "white" }} />
                <Bar dataKey="medianDR" name="Median DR" radius={[3, 3, 0, 0]}>
                  {CHART_DATA.map(entry => <Cell key={entry.name} fill={drColor(entry.medianDR)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Table */}
      <section style={{ background: "#0a0a0a", padding: isMobile ? "0 0 60px" : "0 72px 80px" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "20px", padding: isMobile ? "0 20px" : "0", alignItems: "center" }}>
            <input type="text" placeholder="Search industry…" value={search} onChange={e => setSearch(e.target.value)} style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", color: "white", fontFamily: ff, fontSize: "14px", padding: "10px 14px", outline: "none", width: isMobile ? "100%" : "200px" }} />
            <select value={diffFilter} onChange={e => setDiffFilter(e.target.value)} style={selectStyle}>
              <option value="All">All difficulties</option>
              <option value="Medium">Medium</option>
              <option value="Hard">Hard</option>
              <option value="Very Hard">Very Hard</option>
            </select>
            <div style={{ color: "rgba(255,255,255,0.3)", fontSize: "13px", fontFamily: ff, marginLeft: "auto" }}>{filtered.length} of {DATA.length} industries</div>
          </div>
          <div style={{ overflowX: "auto", borderRadius: "16px", border: "1px solid rgba(255,255,255,0.07)", margin: isMobile ? "0 20px" : "0" }}>
            <table style={{ width: "100%", minWidth: "720px", borderCollapse: "collapse", background: "#111", fontFamily: ff }}>
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.02)" }}>
                  <th style={{ ...thStyle, minWidth: "180px" }} onClick={() => handleSort("industry")}>Industry <SortArrow active={sortCol === "industry"} dir={sortDir} /></th>
                  <th style={{ ...thStyle, minWidth: "110px" }} onClick={() => handleSort("medianDR")}>Median DR <SortArrow active={sortCol === "medianDR"} dir={sortDir} /></th>
                  <th style={{ ...thStyle, minWidth: "90px" }} onClick={() => handleSort("p25DR")}>P25 DR <SortArrow active={sortCol === "p25DR"} dir={sortDir} /></th>
                  <th style={{ ...thStyle, minWidth: "90px" }} onClick={() => handleSort("p75DR")}>P75 DR <SortArrow active={sortCol === "p75DR"} dir={sortDir} /></th>
                  <th style={{ ...thStyle, minWidth: "80px" }} onClick={() => handleSort("minDR")}>Min DR <SortArrow active={sortCol === "minDR"} dir={sortDir} /></th>
                  <th style={{ ...thStyle, minWidth: "80px" }} onClick={() => handleSort("maxDR")}>Max DR <SortArrow active={sortCol === "maxDR"} dir={sortDir} /></th>
                  <th style={{ ...thStyle, minWidth: "110px", cursor: "default" }}>Difficulty</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: "center", color: "rgba(255,255,255,0.3)", fontSize: "14px", padding: "48px" }}>No results.</td></tr>}
                {filtered.map((row, i) => {
                  const dc = diffColors(row.difficultyRating);
                  const mc = drColor(row.medianDR);
                  return (
                    <tr key={row.industry} style={{ background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }} onMouseEnter={e => (e.currentTarget.style.background = "rgba(87,215,226,0.03)")} onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)")}>
                      <td style={tdStyle}>
                        <div style={{ color: "white", fontSize: "13px", fontWeight: 700, marginBottom: "6px" }}>{row.industry}</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                          {row.sampleKeywords.map(kw => <span key={kw} style={{ display: "inline-block", padding: "1px 7px", borderRadius: "3px", background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.35)", fontSize: "10px" }}>{kw}</span>)}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ color: mc, fontSize: "22px", fontWeight: 800 }}>{row.medianDR}</span>
                        <div style={{ width: "60px", height: "4px", borderRadius: "2px", background: "rgba(255,255,255,0.08)", marginTop: "5px" }}>
                          <div style={{ width: `${row.medianDR}%`, height: "100%", borderRadius: "2px", background: mc }} />
                        </div>
                      </td>
                      <td style={tdStyle}><span style={{ color: "rgba(255,255,255,0.6)", fontSize: "16px", fontWeight: 600 }}>{row.p25DR}</span></td>
                      <td style={tdStyle}><span style={{ color: "rgba(255,255,255,0.6)", fontSize: "16px", fontWeight: 600 }}>{row.p75DR}</span></td>
                      <td style={tdStyle}><span style={{ color: "rgba(255,255,255,0.4)", fontSize: "14px" }}>{row.minDR}</span></td>
                      <td style={tdStyle}><span style={{ color: "rgba(255,255,255,0.4)", fontSize: "14px" }}>{row.maxDR}</span></td>
                      <td style={tdStyle}><span style={{ display: "inline-block", padding: "3px 9px", borderRadius: "4px", background: dc.bg, border: `1px solid ${dc.border}`, color: dc.color, fontSize: "11px", fontWeight: 700 }}>{row.difficultyRating}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section style={{ background: "#0a0a0a", padding: isMobile ? "0 20px 56px" : "0 72px 72px" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ padding: isMobile ? "24px" : "28px 32px", borderRadius: "12px", border: "1px solid rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.02)" }}>
            <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "13px", fontFamily: ff, lineHeight: 1.75, margin: 0 }}>
              <span style={{ color: "rgba(255,255,255,0.7)", fontWeight: 700 }}>Methodology:</span>{" "}
              Domain Rating (DR) was recorded for each of the top-10 organic results across 5 representative keywords per industry (55 SERPs × 10 results = 550 data points). DR data sourced from Ahrefs, March 2026. Median, P25, P75, min, and max are calculated across all 50 result pages per industry. DR reflects domain-level authority, not page-level. A site with DR below the P25 benchmark for its category faces a significant organic headwind.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: "#0c0c0c", borderTop: "1px solid rgba(255,255,255,0.05)", padding: isMobile ? "60px 20px" : "80px 72px" }}>
        <div style={{ maxWidth: "640px", margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ color: "white", fontSize: isMobile ? "26px" : "36px", fontWeight: 700, fontFamily: ff, lineHeight: 1.15, marginBottom: "16px" }}>Is your domain authority strong enough to compete?</h2>
          <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "17px", fontFamily: ff, lineHeight: 1.6, marginBottom: "32px" }}>We help B2B companies understand whether they're competitive for their target keywords — and build the domain authority and content strategy to get there.</p>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0" }}>
            <a href={STRATEGIST_URL} style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "16px 32px", background: "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)", borderRadius: "88px", border: "1px solid #57d7e2", boxShadow: "0px 4px 20px rgba(87,215,226,0.25)", textDecoration: "none", color: "#0a0c0c", fontSize: "15px", fontWeight: 700, fontFamily: ff, transition: "opacity 0.2s, transform 0.2s" }} onMouseEnter={e => { e.currentTarget.style.opacity = "0.88"; e.currentTarget.style.transform = "translateY(-1px)"; }} onMouseLeave={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "translateY(0)"; }}>
              Get my free video review →
            </a>
            <a
              href="/link-building"
              style={{ color: "rgba(255,255,255,0.4)", fontSize: "13px", fontFamily: ff, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "5px", marginTop: "16px" }}
              onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.color = "#57d7e2"}
              onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.color = "rgba(255,255,255,0.4)"}
            >
              Learn about our Link Building service
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
